"use client";

// Client because the pile is interactive: a layer clicked comes to the front (useState), the 3D of the pile follows the
// scroll, and the card under the mouse follows the pointer — both through CSS variables written in a
// requestAnimationFrame (useEffect). Everything else here is static text, server-rendered as before: with JS off the
// whole block still reads, the pile only loses its scroll 3D, its pointer lean and its clicks.

import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type RefObject } from "react";
import Tag from "@/components/ds/tag";
import Reveal from "@/components/motion/reveal";
import { stagger } from "@/components/motion/stagger";
import { brandText, type Brand } from "./brand";
import "@/components/ds/button.css"; // .ds-edge: the gradient stroke of the glass cards
import "./firm-brain.css";

// Slide 8 of the deck ("The firm brain"), word for word: the content Jérémy cleared for the public site on 17/09.
// The second line of each layer comes from the same slide; it only shows when that card is at the front (18/09).
const LAYERS = [
  { label: "Emails", line: "Every conversation" },
  { label: "Documents", line: "The firm's corpus" },
  { label: "Matters", line: "People, work, history" },
  { label: "Meeting recordings", line: "What was said, retained" },
  { label: "Custom skills", line: "Your way of working" },
] as const;

// Depth in the pile: 0 is the head card in front, 5 the last layer at the back.
// HEAD: the head card is not one of the five clickable layers, so it takes an index none of them can have.
const HEAD = -1;
const depth = (level: number) => ({ "--level": level }) as CSSProperties;

/**
 * Feeds the pile its place on screen, as two numbers read by firm-brain.css:
 * --p from +1 (the pile's middle sits on the bottom edge of the viewport) through 0 (dead centre, where one reads it:
 * the pile is upright and the text sharp) to −1 (top edge), and --pa, its distance from that centre.
 * One requestAnimationFrame per scroll, nothing while the pile is off screen or the tab hidden, nothing at all under
 * reduced motion or without JS: the stylesheet then keeps its resting values (a gentle, constant tilt).
 */
function useScrollTilt(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const stack = ref.current;
    if (!stack) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    let onScreen = true;
    // Under 720 px the pile is a flat vertical stack with no perspective (firm-brain.css): nothing to feed.
    const flat = window.matchMedia("(max-width: 720px)");

    const update = () => {
      frame = 0;
      if (flat.matches) return;
      const rect = stack.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      const p = Math.max(-1, Math.min(1, (2 * middle) / (window.innerHeight || 1) - 1));
      stack.style.setProperty("--p", p.toFixed(3));
      stack.style.setProperty("--pa", Math.abs(p).toFixed(3));
    };
    const schedule = () => {
      if (!frame && onScreen && !document.hidden) frame = requestAnimationFrame(update);
    };

    // Off screen or hidden tab: no work at all, and the pile keeps the last values it was drawn with.
    const io =
      "IntersectionObserver" in window
        ? new IntersectionObserver(([entry]) => {
            onScreen = entry?.isIntersecting ?? true;
            schedule();
          })
        : null;
    io?.observe(stack);

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.addEventListener("visibilitychange", schedule);
    flat.addEventListener("change", schedule);
    return () => {
      cancelAnimationFrame(frame);
      io?.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("visibilitychange", schedule);
      flat.removeEventListener("change", schedule);
    };
  }, [ref]);
}

/**
 * While the mouse is inside the pile, the card under it follows the pointer: a few pixels towards it, and a light
 * lean towards the corner being pointed at. Read from the pointer's place INSIDE the card — for a layer, inside the
 * strip it actually shows, since the rest of it is under the card in front — and written as four custom properties
 * that firm-brain.css turns into a translate and a perspective() tilt; the CSS transition does the smoothing.
 * One requestAnimationFrame per batch of moves, never a calculation per pixel. Leaving the pile's container puts
 * everything back to zero (Jérémy, 18/09). Mouse only, nothing under reduced motion, nothing while off screen.
 */
function usePointerLean(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const stack = ref.current;
    if (!stack) return;
    const mouse = window.matchMedia("(hover: hover) and (pointer: fine)");
    const still = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mouse.matches || still.matches) return;

    let frame = 0;
    let pending: { card: HTMLElement; x: number; y: number } | null = null;
    let leaning: HTMLElement | null = null;
    let onScreen = true;

    const clear = (card: HTMLElement | null) => {
      if (!card) return;
      for (const name of ["--hx", "--hy", "--hrx", "--hry"]) card.style.removeProperty(name);
    };

    const apply = () => {
      frame = 0;
      const move = pending;
      pending = null;
      if (!move || !onScreen || document.hidden) return;
      const { card, x, y } = move;
      // The card's own box: offsetWidth / offsetHeight are its untransformed size, and offsetX / offsetY the
      // pointer inside it (the browser undoes the pile's 3D for us). A layer only shows its bottom strip.
      const width = card.offsetWidth;
      const height = card.offsetHeight;
      const strip = card.classList.contains("v2-brain__card--layer")
        ? parseFloat(getComputedStyle(card).getPropertyValue("--dy")) || height
        : height;
      const nx = Math.max(-1, Math.min(1, (2 * x) / width - 1));
      const ny = Math.max(-1, Math.min(1, (2 * (y - (height - strip))) / strip - 1));
      if (leaning && leaning !== card) clear(leaning);
      leaning = card;
      card.style.setProperty("--hx", `${(nx * 7).toFixed(2)}px`);
      card.style.setProperty("--hy", `${(ny * 5).toFixed(2)}px`);
      card.style.setProperty("--hrx", `${(-ny * 4.5).toFixed(2)}deg`);
      card.style.setProperty("--hry", `${(nx * 5).toFixed(2)}deg`);
    };

    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      const target = event.target as HTMLElement | null;
      // The cards' contents are pointer-events: none, so the target IS the card whenever the pointer is over one.
      const card = target?.classList.contains("v2-brain__card") ? target : null;
      if (!card) {
        pending = null;
        clear(leaning);
        leaning = null;
        return;
      }
      pending = { card, x: event.offsetX, y: event.offsetY };
      if (!frame) frame = requestAnimationFrame(apply);
    };

    const onLeave = () => {
      pending = null;
      clear(leaning);
      leaning = null;
    };

    const io =
      "IntersectionObserver" in window
        ? new IntersectionObserver(([entry]) => {
            onScreen = entry?.isIntersecting ?? true;
            if (!onScreen) onLeave();
          })
        : null;
    io?.observe(stack);

    stack.addEventListener("pointermove", onMove);
    stack.addEventListener("pointerleave", onLeave);
    document.addEventListener("visibilitychange", onLeave);
    return () => {
      cancelAnimationFrame(frame);
      io?.disconnect();
      stack.removeEventListener("pointermove", onMove);
      stack.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", onLeave);
      clear(leaning);
    };
  }, [ref]);
}

/**
 * The wave that runs down the pile lives in firm-brain.css and never ends; this only tells it when to hold still.
 * data-rest goes on the pile whenever it is off screen or the tab is hidden, and the stylesheet pauses the animation
 * there — paused, not cancelled: the cards stay exactly where they were and take the wave up again from that point.
 * Nothing to do under reduced motion or without JS: there is no wave to pause then (Jérémy, 18/09).
 */
function useWaveGate(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const stack = ref.current;
    if (!stack) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) return;

    let onScreen = true;
    const sync = () => stack.toggleAttribute("data-rest", !onScreen || document.hidden);
    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry?.isIntersecting ?? true;
      sync();
    });
    io.observe(stack);
    document.addEventListener("visibilitychange", sync);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
      stack.removeAttribute("data-rest");
    };
  }, [ref]);
}

/* `brand` (brand.ts): "north" unless the /logo-test preview says "habit". */
export default function FirmBrain({ brand = "north" }: { brand?: Brand }) {
  const stack = useRef<HTMLDivElement>(null);
  // Index of the layer brought in front of the pile (0 to 4), or null for the resting order.
  const [front, setFront] = useState<number | null>(null);

  useScrollTilt(stack);
  usePointerLean(stack);
  useWaveGate(stack);

  // Leaving the pile's container puts it back in order too, next to the second click, another card and Escape.
  // Mouse only: a touch fires pointerleave right after the tap, which would make a second tap unable to close.
  const onStackLeave = useCallback((event: ReactPointerEvent) => {
    if (event.pointerType === "mouse") setFront(null);
  }, []);

  // Escape puts the pile back in order, wherever the focus is (a click does not focus a button on every browser).
  useEffect(() => {
    if (front === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFront(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [front]);

  const toggle = useCallback((index: number) => setFront((current) => (current === index ? null : index)), []);

  // data-front: this card comes forward. data-back: the others step back and dim a little.
  // The head card carries HEAD, an index no layer has: it is never the chosen one, it only steps back.
  const slotState = (index: number) => ({
    "data-front": front === index ? "" : undefined,
    "data-back": front !== null && front !== index ? "" : undefined,
  });

  return (
    // No id: the nav keeps "OS" current over this block (the last nav section above the line is still #northos).
    <section className="v2-brain">
      <div className="v2-brain__inner">
        {/* One block of text, once (Jérémy, 18/09): the heading that used to stand above the pile is gone, and the
            column on the right of the illustration opens the section on its own — tag, title, paragraphs. */}
        <Reveal className="v2-brain__body" amount={0.35}>
          <div className="v2-brain__grid">
            <div className="v2-brain__stack" ref={stack} onPointerLeave={onStackLeave}>
              {/* The pile, in real 3D: layers arrive flat from 05 to 01, the head card lands last, then the plane
                  tips with the scroll (--p), the card under the mouse leans towards the pointer, and a clicked
                  layer comes forward. */}
              <div className="v2-brain__plane">
                <div className="v2-brain__slot" style={depth(0)} {...slotState(HEAD)}>
                  <div className="v2-brain__card v2-brain__card--head ds-edge">
                    <span className="v2-brain__sheen" aria-hidden="true" />
                    {/* Not a heading: the section's only heading is the h2 on the right, and this card is one of six
                        in an illustration — the five others name themselves in a span. An <h3> here would come before
                        that h2 in the source (the pile is read first, on every width) and invert the two levels. */}
                    <p className="v2-brain__name">Firm brain</p>
                    <p className="v2-brain__sub">Your firm&apos;s memory, connected.</p>
                  </div>
                </div>
                <ol className="v2-brain__layers">
                  {LAYERS.map(({ label, line }, i) => (
                    <li key={label} className="v2-brain__slot" style={depth(i + 1)} {...slotState(i)}>
                      <button
                        type="button"
                        className="v2-brain__card v2-brain__card--layer ds-edge"
                        aria-pressed={front === i}
                        aria-label={`0${i + 1} ${label} — ${line} — bring this layer to the front of the pile`}
                        onClick={() => toggle(i)}
                      >
                        {/* The opaque face of a card at the front: a layer of its own, faded in, so nothing animates
                            a background. Then the head card's very layout — the label at the
                            size of "Firm brain", its line at the size of "Your firm's memory, connected." */}
                        <span className="v2-brain__fill" aria-hidden="true" />
                        <span className="v2-brain__row">
                          {/* The number names the layer inside the pile and leaves when the card comes to the front:
                              the head card has no number. Out of the row's flow, so its going moves nothing. */}
                          <span className="v2-brain__num">{String(i + 1).padStart(2, "0")}</span>
                          <span className="v2-brain__label">
                            <span className="v2-brain__label-text">{label}</span>
                            <span className="v2-brain__sub-line">{line}</span>
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="v2-brain__copy">
              {/* The section's tag, which used to open the block above the pile. Its reveal rank (--i 9) is set in
                  firm-brain.css: the design system Tag takes no style of its own. */}
              {/* Jérémy, 18/09: "North OS" no longer appears in any tag or label of the site. */}
              <Tag variant="blue" className="v2-brain__tag v2-reveal">
                The tech
              </Tag>
              <h2 className="v2-brain__title v2-reveal" style={stagger(10)}>
                Your <em>brain.</em>
              </h2>
              {/* Every word slide 8 gave the block, in paragraphs of one make: the lead of the old heading first, then
                  the three that already closed it — "Knowledge that builds on itself.", the right column's own title
                  until now, closes the third one, where it says the same thing as the two sentences before it. */}
              <p className="v2-brain__text v2-reveal" style={stagger(11)}>
                Everything your firm knows. Connected in one living knowledge system.
              </p>
              <p className="v2-brain__text v2-reveal" style={stagger(12)}>
                {brandText("Emails, documents, matters, meeting recordings and custom skills, held together in North OS.", brand)}
              </p>
              <p className="v2-brain__text v2-reveal" style={stagger(13)}>
                Every layer adds context. The brain keeps growing. Knowledge that builds on itself.
              </p>
              <p className="v2-brain__text v2-reveal" style={stagger(14)}>
                The knowledge stays with the firm. The intelligence compounds.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
